import { useState, useEffect, useMemo, useRef } from "react";
import { Drawer } from "../../shared";
import { TextInput } from "../../atoms";
import {
  useReplaceScreenMutation,
  useLazyGetDeviceDetailQuery,
} from "../../../store";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Ekran Değişimi drawer'ı — admin hedef cihazi seri no ile arayip
 * dogrulukten emin olduktan sonra onayar.
 *
 * Akis:
 *  1. Seri no input
 *  2. "Ara" → server'da exact serial match arar (devicesShared.deviceDetail)
 *  3. Bulunursa cihaz bilgisi (serial, firm, model, status) kart olarak gosterilir
 *  4. "Onayla" sadece dogrulanmis cihazda aktif
 */
export default function ScreenReplaceDrawer({ open, pending: pendingProp, onClose, onSuccess }) {
  // Kapanış animasyonu boyunca bekleyen cihaz kaydı dondurulur.
  const { mounted, data: pending } = useDrawerTransition(open, pendingProp);
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const [replace, { isLoading }] = useReplaceScreenMutation();
  const [
    triggerSearch,
    { isFetching: isSearching, data: searchData },
  ] = useLazyGetDeviceDetailQuery();

  const [serialInput, setSerialInput] = useState("");
  const [confirmedDevice, setConfirmedDevice] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSerialInput("");
      setConfirmedDevice(null);
      setShowDropdown(false);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open]);

  const trimmedSerial = serialInput.trim().toUpperCase();

  // Debounce: kullanici yazmaya basladiktan SEARCH_DEBOUNCE_MS sonra
  // server'a istek atilir. confirmedDevice ile yazilan farkli ise
  // dropdown acik kalir, secim bekler.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open) return;
    if (confirmedDevice && confirmedDevice.serialNo === trimmedSerial) {
      // kullanici dropdown'dan secim yapti, input ayni — arama yapma
      return;
    }
    if (trimmedSerial.length < 3) {
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      triggerSearch({
        token: auth?.accessToken,
        serialNo: trimmedSerial,
        page: 1,
        limit: 8,
      });
      setShowDropdown(true);
    }, SEARCH_DEBOUNCE_MS);
  }, [trimmedSerial, open, auth?.accessToken, triggerSearch, confirmedDevice]);

  // Dropdown'da gosterilecek liste — server response shape:
  // { status, devices: [...], pagination } veya { data: { devices: [...] } }
  const candidates = useMemo(() => {
    if (!searchData) return [];
    const list =
      searchData.devices ||
      searchData.data?.devices ||
      searchData.data?.items ||
      (Array.isArray(searchData.data) ? searchData.data : null) ||
      [];
    return list.slice(0, 8);
  }, [searchData]);

  const handleSelectDevice = (device) => {
    setConfirmedDevice(device);
    setSerialInput(device.serialNo || "");
    setShowDropdown(false);
  };

  const handleClearSelection = () => {
    setConfirmedDevice(null);
    setSerialInput("");
  };

  const handleSubmit = async () => {
    if (!confirmedDevice?._id) {
      showError("Önce hedef cihazı arayıp seç.");
      return;
    }
    try {
      const result = await replace({
        token: auth?.accessToken,
        id: pending._id,
        deviceId: confirmedDevice._id,
      }).unwrap();
      showSuccess(
        result?.data?.hasBackup
          ? `Ekran değişimi tamamlandı (backup yüklenecek): ${result?.data?.serialNo}`
          : `Ekran değişimi tamamlandı (backup yok): ${result?.data?.serialNo}`
      );
      onSuccess?.(result);
      onClose();
    } catch (err) {
      showError(err?.data?.message || "Atama başarısız.");
    }
  };

  if (!mounted || !pending) return null;
  const showNotFound =
    showDropdown && !isSearching && trimmedSerial.length >= 3 && candidates.length === 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      width="md"
      title="Ekran Değişimi Onayı"
    >
      <Drawer.Body className="px-5 py-4">
        <div className="space-y-4">
          <div className="bg-input/30 rounded-lg p-3 text-sm">
            <div className="text-xs uppercase tracking-wider text-mainSecond/60">
              Doğrulama Kodu
            </div>
            <div className="font-mono font-bold text-2xl text-main mt-1">
              {pending.code}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-mainSecond/50 mt-2">
              Ön Kayıt ID
            </div>
            <div className="text-xs text-mainSecond/60 break-all font-mono">
              {pending.tempClientId}
            </div>
          </div>

          <div className="relative">
            <TextInput
              label="Hedef Seri No *"
              value={serialInput}
              onChange={(e) => {
                setSerialInput(e.target.value.toUpperCase());
                if (confirmedDevice) setConfirmedDevice(null);
              }}
              onFocus={() => {
                if (candidates.length > 0 && !confirmedDevice) {
                  setShowDropdown(true);
                }
              }}
              placeholder="BYN... (en az 3 karakter yaz, dropdown'dan seç)"
            />

            {/* Dropdown — debounced search sonuçları */}
            {showDropdown && trimmedSerial.length >= 3 && !confirmedDevice && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-input rounded-lg shadow-lg z-10 max-h-72 overflow-y-auto">
                {isSearching && (
                  <div className="px-3 py-2 text-xs text-mainSecond/60">
                    Aranıyor...
                  </div>
                )}
                {!isSearching && candidates.length === 0 && (
                  <div className="px-3 py-3 text-xs text-mainSecond/60">
                    <strong>{trimmedSerial}</strong> ile eşleşen cihaz yok.
                  </div>
                )}
                {!isSearching &&
                  candidates.map((d) => (
                    <button
                      key={d._id}
                      type="button"
                      onClick={() => handleSelectDevice(d)}
                      className="w-full text-left px-3 py-2 hover:bg-input/30 border-b border-input/40 last:border-b-0 transition"
                    >
                      <div className="font-mono text-sm font-semibold text-main">
                        {d.serialNo}
                      </div>
                      <div className="text-[11px] text-mainSecond/60 mt-0.5 flex flex-wrap gap-x-2">
                        {d.machineName && <span>{d.machineName}</span>}
                        {d.firm?.firmName && (
                          <span className="truncate">
                            • {d.firm.firmName}
                          </span>
                        )}
                        {d.deviceStatusName && (
                          <span>• {d.deviceStatusName}</span>
                        )}
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {confirmedDevice && (
            <div className="bg-sixth/10 border border-sixth/30 rounded-lg p-3 space-y-2 relative">
              <button
                type="button"
                onClick={handleClearSelection}
                className="absolute top-2 right-2 text-mainSecond/50 hover:text-fifth text-xs"
              >
                Değiştir
              </button>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-sixth" />
                <span className="text-xs uppercase tracking-wider font-semibold text-sixth">
                  Hedef Cihaz Seçildi
                </span>
              </div>
              <div className="grid grid-cols-1 gap-1.5 text-xs">
                <div>
                  <span className="text-mainSecond/60">Seri No:</span>{" "}
                  <span className="font-mono font-semibold text-main">
                    {confirmedDevice.serialNo}
                  </span>
                </div>
                {confirmedDevice.machineName && (
                  <div>
                    <span className="text-mainSecond/60">Makine:</span>{" "}
                    <span className="text-main">
                      {confirmedDevice.machineName}
                    </span>
                  </div>
                )}
                {confirmedDevice.firm?.firmName && (
                  <div>
                    <span className="text-mainSecond/60">Firma:</span>{" "}
                    <span className="text-main">
                      {confirmedDevice.firm.firmName}
                    </span>
                  </div>
                )}
                {confirmedDevice.deviceStatusName && (
                  <div>
                    <span className="text-mainSecond/60">Durum:</span>{" "}
                    <span className="text-main">
                      {confirmedDevice.deviceStatusName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-xs text-mainSecond/60 leading-relaxed bg-newtwo/40 rounded-lg p-3">
            Doğrulanan cihazın serialNo'su ve identity bilgisi makineye
            gönderilecek. Sunucuda backup varsa indirme URL'i de paylaşılır
            (5 dk geçerli signed URL); yoksa makine yalnızca serialNo ile
            başlar.
          </div>
        </div>
      </Drawer.Body>
      <Drawer.Footer className="border-t border-input px-5 py-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg border border-input text-mainSecond"
          disabled={isLoading}
        >
          Vazgeç
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !confirmedDevice}
          className="px-4 py-2 rounded-lg bg-main text-new font-medium hover:bg-mainSecond disabled:opacity-50"
        >
          {isLoading ? "Onaylanıyor..." : "Onayla"}
        </button>
      </Drawer.Footer>
    </Drawer>
  );
}
